import { memo, useCallback } from 'react';

import { useDrag } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { cn } from '@/lib/utils';
import { selectHasError } from '@/redux/dialogAlerts/selectors';
import { TYPES } from '@/redux/dialogs/helper';
import { popModal, pushModal } from '@/redux/modals/actions';
import { removeAction, selectAction } from '@/redux/nodes/actions';
import {
  selectIsActionSelected,
  selectIsIdInFlow,
  selectIsNodeConditionRequisiteSelected,
} from '@/redux/nodes/selectors';
import { AIResponseSubAgentConfig, SubAgentAction } from '@/shared/types/action';
import { isKeyEnter, preventClickThrough } from '@/util/util';

import ActionHeader from '../../ActionHeader/ActionHeader';
import { BoxErrorIcon } from '../Action/Action';
import ActionPlaceholder from '../Placeholder/Placeholder';

interface Props {
  action: SubAgentAction;
  actionId: string;
  nodeId: string;
  requisiteIndex?: number;
  conditionIndex?: number;
}

const AIResponseActionCard = memo(
  ({ action, actionId, nodeId, requisiteIndex = null, conditionIndex = null }: Props) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isSelected = useSelector(selectIsActionSelected(actionId));
    const isActionInFlow = useSelector(selectIsIdInFlow(actionId));
    const isAnythingSelected = useSelector(selectIsNodeConditionRequisiteSelected);
    const hasDialogError = useSelector(selectHasError(actionId));

    const guidelines = (action.sub_agent_config as AIResponseSubAgentConfig)?.guidelines ?? '';

    const [, dragRef] = useDrag({
      item: { actionId, nodeId, requisiteIndex, conditionIndex, move: true, type: action.type },
      type: TYPES.ACTION,
      canDrag: () => !document?.activeElement?.className?.match(/DraftEditor|input/),
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    const handleSelect = useCallback(() => {
      if (!isSelected) {
        dispatch(selectAction({ actionId }));
      }
    }, [actionId, dispatch, isSelected]);

    const handleBoxClick = useCallback(
      (e: React.MouseEvent) => {
        preventClickThrough(e);
        handleSelect();
      },
      [handleSelect]
    );

    const handleBoxKeyUp = useCallback(
      (e: React.KeyboardEvent) => {
        if (isKeyEnter(e)) {
          handleSelect();
        }
      },
      [handleSelect]
    );

    const handleDelete = useCallback(() => {
      dispatch(
        pushModal(MODAL_DELETE, {
          title: t('dialog.ai_response.remove_title'),
          subtitle: t('dialog.ai_response.remove_subtitle'),
          onDelete: () => {
            dispatch(removeAction({ actionId }));
            dispatch(popModal());
          },
        })
      );
    }, [actionId, dispatch, t]);

    return (
      <>
        <ActionPlaceholder
          nodeId={nodeId}
          beforeActionId={actionId}
          requisiteIndex={requisiteIndex}
          conditionIndex={conditionIndex}
        />

        <section
          id={actionId}
          className={cn(
            'relative mx-auto mt-[13px] w-60',
            'before:absolute before:bottom-full before:left-1/2 before:z-[1] before:h-[18px] before:w-2 before:-translate-x-1/2',
            "before:bg-[image:url('/connecting-line.svg')] before:bg-no-repeat before:content-['']"
          )}
        >
          {hasDialogError && <BoxErrorIcon />}
          <div
            ref={(node) => {
              dragRef(node);
            }}
            className={cn(
              'relative flex w-60 flex-col items-stretch rounded border bg-background transition',
              isSelected ? 'border-informational' : 'border-stroke',
              isSelected && hasDialogError && 'border-destructive ring-2 ring-destructive',
              !isActionInFlow && !isAnythingSelected && 'opacity-50'
            )}
            role="button"
            tabIndex={0}
            onClick={handleBoxClick}
            onKeyUp={handleBoxKeyUp}
            data-testid="ai_response-action-card"
          >
            <ActionHeader
              type="ai_response"
              objectToCopy={action}
              isSelected={isSelected}
              onDelete={handleDelete}
              hasDialogError={hasDialogError}
            />
            {guidelines && (
              <div className="p-3">
                <p className="line-clamp-3 text-sm leading-5 text-foreground">{guidelines}</p>
              </div>
            )}
          </div>
        </section>
      </>
    );
  }
);

AIResponseActionCard.displayName = 'AIResponseActionCard';

export default AIResponseActionCard;
